import React, { useMemo } from 'react';
import { renderMarkdown } from '../../lib/formatters';
import { agentInfo } from '../../lib/agentRegistry';
import { api } from '../../lib/api';
import { Chat, Cluster, Text, Avatar, Chip, Icon } from '@iqueue/iq-ui';

/**
 * One row of the transcript on top of iq-ui Chat.Message/Bubble.
 *
 * ONE component renders BOTH a live run and the durable message it becomes:
 * the contract asks the working row to morph into the arriving reply, and a
 * morph needs the same element TYPE and the same React key on both sides of
 * the flip (Chat do: «render a reply that has not arrived as a Chat.Message
 * with `pending` and the SAME key it will keep»; dont: «don't render the
 * working indicator as a separate sibling of the message it precedes»). A
 * live run arrives here as a message-shaped row with `live: true`.
 */
export default React.memo(function Message({
  msg, me, position = 'single', onReply, onCancelRun, onRetrySend, onDropMessage,
}) {
  const isLive = !!msg.live;
  const senderType = msg.sender_type || (msg.type === 'user' ? 'user' : 'agent');
  const isSystem = msg.sender === 'system' || msg.type === 'system';
  const isOwn = senderType === 'user' && msg.sender === me;
  const senderId = msg.sender || 'system';
  const d = senderType === 'user' ? { name: senderId } : agentInfo(senderId);
  const time = new Date(msg.ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const textHtml = useMemo(() => renderMarkdown(msg.text), [msg.text]);
  const attachments = Array.isArray(msg.attachments) ? msg.attachments : [];
  const tools = isLive && Array.isArray(msg.tools) ? msg.tools : [];

  if (isSystem) {
    return <Chat.Separator label={msg.text} />;
  }

  const parent = msg.parent_sender;
  const author = (
    <Text variant="label-medium" render={<span />}>
      {d.name}
      {parent && parent !== senderId && (
        <Text variant="label-small" muted render={<span />}> via @{parent}</Text>
      )}
      {isLive && (
        <Text variant="label-small" muted render={<span />}> {msg.done ? 'завершено' : 'работает…'}</Text>
      )}
    </Text>
  );

  const images = attachments.filter(a => a.mime && a.mime.startsWith('image/'));
  const files = attachments.filter(a => !a.mime || !a.mime.startsWith('image/'));

  // Every action is reachable from the menu; `quick` only promotes it to the
  // hover panel (Chat dont: «don't put an action ONLY in the quick panel»).
  const actions = [];
  if (isLive && !msg.done && onCancelRun) {
    actions.push({ id: 'stop', label: 'Остановить', icon: 'stop_circle', quick: true, onSelect: () => onCancelRun(msg.run) });
  }
  if (!isLive && onReply && msg.message_id) {
    actions.push({ id: 'reply', label: 'Ответить', icon: 'reply', quick: true, onSelect: () => onReply(msg) });
  }
  if (msg.failed && onRetrySend) {
    actions.push({ id: 'retry', label: 'Отправить ещё раз', icon: 'refresh', quick: true, onSelect: () => onRetrySend(msg) });
  }
  if (msg.failed && onDropMessage) {
    actions.push({ id: 'drop', label: 'Удалить', icon: 'delete', onSelect: () => onDropMessage(msg) });
  }
  if (msg.text) {
    actions.push({ id: 'copy', label: 'Копировать текст', icon: 'content_copy', onSelect: () => navigator.clipboard?.writeText(msg.text) });
  }

  const hasContent = !isLive || !!(msg.text || tools.length);
  const wide = !!(msg.text && msg.text.includes('```'));

  return (
    <Chat.Message
      side={isOwn ? 'own' : 'other'}
      position={position}
      author={author}
      time={time}
      timeLabel={new Date(msg.ts).toLocaleString([], { dateStyle: 'full', timeStyle: 'short' })}
      avatar={<Avatar name={d.name} colorFromName />}
      status={isOwn ? (msg.failed ? 'failed' : msg.pending ? 'sending' : 'sent') : undefined}
      pending={!hasContent}
      actions={actions.length ? actions : undefined}
      actionsLabel="Действия с сообщением"
      moreLabel="Ещё"
    >
      {images.length > 0 && (
        <Chat.MediaGroup>
          {images.map((a, i) => (
            <Chat.Media key={a.id || i} src={api.artifactUrl(a.id)} alt={a.name}
              onOpen={() => window.open(api.artifactUrl(a.id), '_blank')} />
          ))}
        </Chat.MediaGroup>
      )}
      {hasContent && (
        <Chat.Bubble width={wide ? 'wide' : 'text'}>
          {files.length > 0 && (
            <Cluster gap={50}>
              {files.map((a, i) => (
                <Chip key={a.id || i} leadingIcon={<Icon name="attach_file" />}
                  onClick={() => window.open(api.artifactUrl(a.id), '_blank')}>
                  {a.name}
                </Chip>
              ))}
            </Cluster>
          )}
          {tools.slice(-6).map((tl, i) => (
            <Cluster key={i} gap={50} align="center" wrap={false}>
              <Icon name="build" size="s" />
              <Text variant="body-small" render={<span />}>{tl.name}</Text>
              {tl.summary ? <Text variant="body-small" muted truncate render={<span />}>{tl.summary}</Text> : null}
            </Cluster>
          ))}
          {isLive && msg.progress && !msg.done && (
            <Text variant="body-small" muted>
              {msg.progress.text || ''}{msg.progress.percent != null ? ` (${msg.progress.percent}%)` : ''}
            </Text>
          )}
          {/* Sanitized markdown needs a host element; it goes through a
              primitive's render prop, so the rendered HTML still gets the MD3
              body typescale instead of the browser default (iq/no-raw-element). */}
          {msg.text ? <Text variant="body-medium" render={<div dangerouslySetInnerHTML={{ __html: textHtml }} />} /> : null}
        </Chat.Bubble>
      )}
    </Chat.Message>
  );
});
